import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ChartRenderer } from '@/components/report-builder/ChartRenderer';
import { TableRenderer } from '@/components/report-builder/TableRenderer';
import { PivotTableRenderer } from '@/components/report-builder/PivotTableRenderer';
import { ConfigPanel } from '@/components/report-builder/ConfigPanel';
import { FieldSplitPanel } from '@/components/report-builder/FieldSplitPanel';
import { BinFieldDialog } from '@/components/report-builder/BinFieldDialog';
import FieldStatsModal from '@/components/FieldStatsModal';
import { DropShelf } from '@/components/report-builder/DropShelf';
import { SlicerControl } from '@/components/report-builder/SlicerControl';
import { BrowseDataDialog } from '@/components/report-builder/BrowseDataDialog';
import { FieldPropertiesDialog } from '@/components/report-builder/FieldPropertiesDialog';
import { resolveLabel } from '@/lib/fieldLabel';
import {
  ArrowLeft, Save, BarChart3, Loader2, Layers, Database, Eye,
  TrendingUp, Sparkles, Filter, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { createFormatValue } from '@/components/report-builder/formatUtils';

const VIZ_TYPES = [
  { value: 'bar_chart', label: 'Bar Chart' }, { value: 'line_chart', label: 'Line Chart' },
  { value: 'area_chart', label: 'Area Chart' }, { value: 'pie_chart', label: 'Pie Chart' },
  { value: 'table', label: 'Table' }, { value: 'pivot', label: 'Pivot Table' },
  { value: 'loss_triangle', label: 'Loss Triangle' },
  { value: 'combo', label: 'Combo' }, { value: 'scatter', label: 'Scatter' },
  { value: 'funnel', label: 'Funnel' }, { value: 'waterfall', label: 'Waterfall' },
  { value: 'treemap', label: 'Treemap' }, { value: 'heatmap', label: 'Heatmap' },
];

export default function VisualEditorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const visualId = searchParams.get('id');
  // Preview mode is triggered by mounting this page under
  // /visual-library/preview — the same component powers both flows so
  // the chart preview logic stays in one place. In preview mode we hide
  // the field list + drop zones + AI/save bar and show only the chart.
  const previewMode = location.pathname.endsWith('/preview');

  // Visual definition
  const [visual, setVisual] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [modelId, setModelId] = useState('');
  const [vizType, setVizType] = useState('bar_chart');
  const [rowDims, setRowDims] = useState([]);
  const [colDim, setColDim] = useState('');
  const [valueMeasures, setValueMeasures] = useState([]);
  const [filterDims, setFilterDims] = useState([]);
  const [vizConfig, setVizConfig] = useState({});
  const [showGrid, setShowGrid] = useState(true);
  const [showLegend, setShowLegend] = useState(true);

  // Resizable left field-list panel (mirrors the Dashboard/Report editor).
  const [fieldsPanelWidth, setFieldsPanelWidth] = useState(() => Number(localStorage.getItem('ve_fields_w')) || 224);
  useEffect(() => { localStorage.setItem('ve_fields_w', String(fieldsPanelWidth)); }, [fieldsPanelWidth]);
  const startFieldsResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = fieldsPanelWidth;
    const onMove = (ev) => setFieldsPanelWidth(Math.max(180, Math.min(480, startW + (ev.clientX - startX))));
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = ''; document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
  };

  // UI state
  const [allFields, setAllFields] = useState([]);
  const [calcMeasureFields, setCalcMeasureFields] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [queryResult, setQueryResult] = useState(null);
  const [pivotResult, setPivotResult] = useState(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [viewDataField, setViewDataField] = useState(null);
  const handleViewField = (field) => setViewDataField(field?.name || null);
  const [binField, setBinField] = useState(null);
  const handleBinField = (field) => setBinField(field);
  const [fieldSearch, setFieldSearch] = useState('');
  const previewRef = useRef(null);

  // AI Build state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBuilding, setAiBuilding] = useState(false);
  const [aiExplanation, setAiExplanation] = useState('');
  const [aiAlternatives, setAiAlternatives] = useState([]);

  // Slicer state
  const [slicerSelections, setSlicerSelections] = useState({});
  const [slicerValues, setSlicerValues] = useState({});

  // Browse data dialog
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseField, setBrowseField] = useState(null);
  const [browseData, setBrowseData] = useState([]);
  const [browseLoading, setBrowseLoading] = useState(false);

  // Field properties dialog
  const [propsOpen, setPropsOpen] = useState(false);
  const [propsField, setPropsField] = useState(null);
  const [propsForm, setPropsForm] = useState({});
  const [fieldOverrides, setFieldOverrides] = useState({});

  // Pill right-click context menu
  const [pillCtxMenu, setPillCtxMenu] = useState(null);

  // Load visual + models
  useEffect(() => {
    const load = async () => {
      try {
        const [m] = await Promise.all([api.getSemanticModels().catch(() => [])]);
        setModels(m);
        if (visualId) {
          const v = await api.getVisual(visualId);
          setVisual(v);
          setName(v.name || '');
          setDescription(v.description || '');
          setModelId(v.model_id || '');
          setVizType(v.viz_type || 'bar_chart');
          setRowDims(v.row_dims || []);
          setColDim(v.col_dim || '');
          setValueMeasures(v.value_measures || []);
          setFilterDims(v.filter_dims || []);
          setSlicerSelections(v.slicer_selections || {});
          setVizConfig(v.viz_config || {});
        } else {
          // New visual — honor a ?model= deep-link (e.g. from AI Explore / a chart's
          // "Edit measure") by preselecting that model if it exists.
          const wantModel = searchParams.get('model');
          if (wantModel && (m || []).some(x => x.model_id === wantModel)) {
            setModelId(wantModel);
          }
        }
      } catch (err) { toast.error('Failed to load visual'); }
      finally { setLoading(false); }
    };
    load();
  }, [visualId, searchParams]);

  // Load model fields when model changes
  // Load model fields when model changes (also re-run after creating a binned field).
  const loadModelFields = useCallback(async () => {
    if (!modelId) return;
    try {
      const m = await api.getSemanticModelFull(modelId);
      const f = [];
      const seen = new Set();
      (m.entities || []).forEach(e => {
        (e.attributes || []).forEach(a => { if (!seen.has(a.name)) { f.push({ ...a, entity_id: e.entity_id, entity_name: e.name, entity_logical_name: e.logical_name || '' }); seen.add(a.name); } });
      });
      // Add calculated measures from the model (stored in semantic_measures collection)
      const cm = [];
      (m.calculated_measures || []).forEach(ms => {
        if (!seen.has(ms.name)) {
          const calcField = { name: ms.name, description: ms.description || ms.name, semantic_type: 'measure', data_type: 'float', aggregation: ms.aggregation || 'sum', formula: ms.formula, is_calculated: true, _isModelMeasure: true };
          cm.push(calcField);
          seen.add(ms.name);
        }
      });
      setAllFields(f);
      setCalcMeasureFields(cm);
    } catch { /* ignore */ }
  }, [modelId]);
  useEffect(() => { loadModelFields(); }, [loadModelFields]);

  // Load slicer values when filterDims change
  useEffect(() => {
    if (!modelId) return;
    filterDims.forEach(dim => {
      // Use functional setState to read latest slicerValues without stale closure
      setSlicerValues(prev => {
        if (prev[dim]) return prev;
        api.executeQuery({ model_id: modelId, dimensions: [dim], measures: [], limit: 500 }).then(res => {
          const vals = [...new Set((res.data || []).map(r => r[dim]).filter(Boolean))].sort();
          setSlicerValues(p => ({ ...p, [dim]: vals }));
        }).catch(() => {});
        return prev;
      });
    });
  }, [modelId, filterDims]);

  // Execute query when fields change
  const executeQuery = useCallback(async () => {
    if (!modelId || (!rowDims.length && !valueMeasures.length)) {
      setQueryResult(null);
      setPivotResult(null);
      return;
    }
    setQueryLoading(true);
    try {
      const dims = [...rowDims, ...(colDim ? [colDim] : [])];
      const topN = vizConfig.topN;
      // A Color/Series dim multiplies row count (dates × series). Fetch a larger
      // window so every date×series combo is present for the client-side pivot.
      const limit = topN?.enabled && topN?.n ? topN.n : (colDim ? 2000 : 200);
      const sortBy = topN?.enabled && topN?.by ? topN.by : undefined;
      const sortDir = topN?.enabled ? (topN?.direction === 'bottom' ? 'asc' : 'desc') : 'asc';
      // Build slicer filters
      const filters = Object.entries(slicerSelections).filter(([_, v]) => v).map(([field, value]) => ({ field, operator: 'equals', value }));
      const res = await api.executeQuery({ model_id: modelId, dimensions: dims, measures: valueMeasures, limit, sort_by: sortBy, sort_dir: sortDir, filters });
      setQueryResult(res);
      if (colDim && rowDims.length > 0 && valueMeasures.length > 0) {
        const pv = await api.pivotQuery({ model_id: modelId, row_dimensions: rowDims, column_dimension: colDim, measures: valueMeasures, limit, filters });
        setPivotResult(pv);
      } else {
        setPivotResult(null);
      }
    } catch { setQueryResult(null); setPivotResult(null); }
    finally { setQueryLoading(false); }
  }, [modelId, rowDims, colDim, valueMeasures, vizConfig.topN, slicerSelections]);

  // Keep the latest executeQuery in a ref so the fetch effect doesn't depend on
  // the callback identity (which can change on unrelated re-renders).
  const executeQueryRef = useRef(executeQuery);
  useEffect(() => { executeQueryRef.current = executeQuery; }, [executeQuery]);

  // Only re-query when a change actually alters the query (fields, color dim,
  // measures, Top-N, or slicer filters). Editing cosmetic config (title, colors,
  // legend, axis labels…) does NOT change this signature, so no re-query fires.
  const querySignature = JSON.stringify({
    m: modelId, r: rowDims, c: colDim, v: valueMeasures,
    t: vizConfig.topN || null, f: slicerSelections,
  });
  useEffect(() => {
    const t = setTimeout(() => executeQueryRef.current(), 300);
    return () => clearTimeout(t);
     
  }, [querySignature]);

  // AI Build Visual
  const handleAiBuild = async () => {
    if (!aiPrompt.trim() || !modelId) return;
    setAiBuilding(true);
    setAiExplanation('');
    setAiAlternatives([]);
    try {
      const currentConfig = rowDims.length > 0 || valueMeasures.length > 0 ? {
        viz_type: vizType, row_dims: rowDims, col_dim: colDim, value_measures: valueMeasures, top_n: vizConfig.topN
      } : undefined;
      const result = await api.aiBuildVisual({ prompt: aiPrompt, model_id: modelId, current_config: currentConfig });
      if (result.error) { toast.error(result.error); return; }
      // Apply the AI result
      if (result.viz_type) { setVizType(result.viz_type); setDirty(true); }
      if (result.title && !name) { setName(result.title); setDirty(true); }
      if (result.row_dims) { setRowDims(result.row_dims); setDirty(true); }
      if (result.col_dim !== undefined) { setColDim(result.col_dim || ''); setDirty(true); }
      if (result.value_measures) { setValueMeasures(result.value_measures); setDirty(true); }
      if (result.top_n) { setVizConfig(prev => ({ ...prev, topN: result.top_n })); setDirty(true); }
      if (result.explanation) setAiExplanation(result.explanation);
      if (result.suggested_alternatives) setAiAlternatives(result.suggested_alternatives || []);
      if (result.title) setName(result.title);
      setAiPrompt('');
      toast.success('Visual configured by AI');
    } catch (err) { toast.error(err.message); }
    finally { setAiBuilding(false); }
  };


  // Save with thumbnail capture
  const handleSave = async () => {
    if (!visualId) return;
    setSaving(true);
    try {
      let thumbnail = '';
      if (previewRef.current && chartData.length > 0) {
        try {
          const html2canvas = (await import('html2canvas')).default;
          const canvas = await html2canvas(previewRef.current, {
            scale: 0.5, useCORS: true, logging: false,
            backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--background').trim() || '#09090b',
          });
          thumbnail = canvas.toDataURL('image/jpeg', 0.7);
          if (thumbnail.length > 300000) {
            const smallCanvas = await html2canvas(previewRef.current, { scale: 0.3, useCORS: true, logging: false, backgroundColor: (document.documentElement.classList.contains('dark') ? '#09090b' : '#ffffff') });
            thumbnail = smallCanvas.toDataURL('image/jpeg', 0.5);
          }
        } catch (e) { console.warn('Thumbnail capture failed:', e); }
      }
      await api.updateVisual(visualId, {
        name, description, model_id: modelId, viz_type: vizType,
        row_dims: rowDims, col_dim: colDim, value_measures: valueMeasures,
        filter_dims: filterDims, viz_config: vizConfig,
        slicer_selections: slicerSelections,
        top_n: vizConfig.topN || null, thumbnail,
      });
      setDirty(false);
      toast.success('Visual saved');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  // Save a copy — mirrors the builder's "Save a copy": clones the visual under a
  // new name and opens the copy so the user keeps editing it (original intact).
  const handleSaveCopy = async () => {
    if (!modelId) { toast.error('Nothing to copy yet'); return; }
    setSaving(true);
    try {
      const res = await api.createVisual({
        name: `${name || 'Visual'} (Copy)`, description, model_id: modelId, viz_type: vizType,
        row_dims: rowDims, col_dim: colDim, value_measures: valueMeasures,
        filter_dims: filterDims, viz_config: vizConfig, slicer_selections: slicerSelections, top_n: vizConfig.topN || null,
      });
      const newId = res?.visual_id || res?.id;
      setDirty(false);
      toast.success(`Saved a copy — "${name || 'Visual'} (Copy)"`);
      if (newId) navigate(`/visual-library/edit?id=${newId}`);
    } catch (err) { toast.error(err.message || 'Could not save a copy'); }
    finally { setSaving(false); }
  };
  // Field helpers
  const allFieldsCombined = [...allFields, ...calcMeasureFields];
  const dimensions = allFields.filter(f => f.semantic_type !== 'measure' && f.semantic_type !== 'hidden' && !f._isModelMeasure);
  const measures = allFields.filter(f => f.semantic_type === 'measure' || f._isModelMeasure);
  const filteredDims = dimensions.filter(f => !fieldSearch || f.name.toLowerCase().includes(fieldSearch.toLowerCase()) || (f.description || '').toLowerCase().includes(fieldSearch.toLowerCase()));
  const filteredMeasures = measures.filter(f => !fieldSearch || f.name.toLowerCase().includes(fieldSearch.toLowerCase()) || (f.description || '').toLowerCase().includes(fieldSearch.toLowerCase()));
  const filteredCalcMsrs = calcMeasureFields.filter(f => !fieldSearch || f.name.toLowerCase().includes(fieldSearch.toLowerCase()) || (f.description || '').toLowerCase().includes(fieldSearch.toLowerCase()));

  // Honor each field's date/number format (model setting + user Properties
  // override). Dates render without a time portion; shared with Report Builder.
  const formatValue = createFormatValue(allFieldsCombined, fieldOverrides);

  const removeFromAllShelves = (fieldName) => {
    setDirty(true);
    setRowDims(p => p.filter(x => x !== fieldName));
    if (colDim === fieldName) setColDim('');
    setValueMeasures(p => p.filter(x => x !== fieldName));
    setFilterDims(p => p.filter(x => x !== fieldName));
  };

  const handleAddFieldToVisual = (fieldName, fieldType) => {
    setDirty(true);
    if (fieldType === 'measure') {
      if (!valueMeasures.includes(fieldName)) setValueMeasures(prev => [...prev, fieldName]);
    } else {
      if (!rowDims.includes(fieldName) && colDim !== fieldName) setRowDims(prev => [...prev, fieldName]);
    }
  };

  const handleBrowseField = async (field) => {
    setBrowseField(field);
    setBrowseOpen(true);
    setBrowseLoading(true);
    setBrowseData([]);
    try {
      const res = await api.executeQuery({ model_id: modelId, dimensions: [field.name], measures: [], limit: 500 });
      setBrowseData(res?.data || []);
    } catch (e) { console.warn('Browse query failed:', e.message); }
    finally { setBrowseLoading(false); }
  };

  const saveFieldProperties = () => {
    if (propsField) {
      setFieldOverrides(prev => ({ ...prev, [propsField.name]: propsForm }));
    }
    setPropsOpen(false);
  };

  const openFieldProperties = (fieldName) => {
    const f = allFieldsCombined.find(ff => ff.name === fieldName);
    setPropsField(f || { name: fieldName });
    const ovr = fieldOverrides[fieldName] || {};
    setPropsForm({ ...ovr, displayName: ovr.displayName || resolveLabel(allFieldsCombined, fieldName) });
    setPropsOpen(true);
  };

  const handlePillRightClick = (x, y, fieldName, shelfLabel) => {
    setPillCtxMenu({ x, y, field: fieldName, shelf: shelfLabel });
  };

  const shelfProps = { allFields: allFieldsCombined, fieldOverrides, onFieldProperties: openFieldProperties, onPillRightClick: handlePillRightClick };

  const currentVisual = { id: 'editor', vizType, title: name, rowDims, colDim, valueMeasures, filterDims };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  }

  const chartData = queryResult?.data || [];

  // When a Color/Series dimension is set, the chart needs WIDE data (one column
  // per series value). Prefer the server pivot; if it didn't pivot, pivot the
  // long-format rows client-side so every series (e.g. each Line of Business)
  // renders — instead of a single collapsed line.
  const { chartDisplayData, chartColDim } = (() => {
    if (pivotResult?.is_pivoted) return { chartDisplayData: pivotResult.data, chartColDim: '' };
    if (colDim && rowDims.length > 0 && valueMeasures.length > 0
        && chartData.length && Object.prototype.hasOwnProperty.call(chartData[0], colDim)) {
      const measure = valueMeasures[0];
      const map = new Map();
      const seriesVals = new Set();
      for (const row of chartData) {
        const xk = rowDims.map(d => String(row[d] ?? '')).join(' | ');
        if (!map.has(xk)) { const base = {}; rowDims.forEach(d => { base[d] = row[d]; }); map.set(xk, base); }
        const sv = String(row[colDim] ?? '—');
        seriesVals.add(sv);
        const rec = map.get(xk);
        rec[sv] = (rec[sv] || 0) + (Number(row[measure]) || 0);
      }
      // Every x-group must carry every series key (default 0). ChartRenderer
      // derives the series list from the FIRST row's keys, so ragged rows would
      // collapse the chart to whichever series the first date happened to have.
      const series = Array.from(seriesVals);
      const rows = Array.from(map.values()).map(rec => {
        series.forEach(s => { if (rec[s] === undefined) rec[s] = 0; });
        return rec;
      });
      return { chartDisplayData: rows, chartColDim: '' };
    }
    return { chartDisplayData: chartData, chartColDim: colDim };
  })();

  return (
    <TooltipProvider>
    <div className="h-full flex flex-col" data-testid={previewMode ? 'visual-preview-page' : 'visual-editor-page'}>
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 bg-muted/10 flex-shrink-0">
        <Button variant="ghost" size="sm" onClick={() => navigate('/visual-library')} className="gap-1.5" data-testid="visual-preview-back">
          <ArrowLeft className="w-4 h-4" /> Library
        </Button>
        <div className="h-5 w-px bg-border/50" />
        <Layers className="w-4 h-4 text-primary" />
        {previewMode ? (
          <>
            <span className="text-sm font-medium text-foreground truncate" data-testid="visual-preview-name">{name || 'Untitled Visual'}</span>
            <Badge variant="outline" className="text-[9px] text-primary border-primary/30">Preview</Badge>
            <div className="flex-1" />
            <Button size="sm" variant="secondary" onClick={() => navigate(`/visual-library/edit?id=${visualId}`)} className="gap-1.5" data-testid="visual-preview-edit-btn">
              Open in editor
            </Button>
          </>
        ) : (
          <>
            <Input value={name} onChange={e => { setName(e.target.value); setDirty(true); }} className="h-8 w-64 text-sm font-medium" data-testid="visual-name-input" />
            <div className="flex-1" />
            {dirty && <Badge variant="outline" className="text-[9px] text-amber-500 border-amber-500/30">Unsaved</Badge>}
            {visualId && (
              <Button size="sm" variant="outline" onClick={handleSaveCopy} disabled={saving || !modelId} className="gap-1.5" data-testid="save-visual-copy-btn">
                <Copy className="w-3.5 h-3.5" /> Save a copy
              </Button>
            )}
            <Button size="sm" onClick={handleSave} disabled={saving} className="gap-1.5" data-testid="save-visual-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </Button>
          </>
        )}
      </div>

      {/* AI Build Bar */}
      {!previewMode && (
      <div className="px-4 py-2 border-b border-border/30 bg-indigo-500/5 flex items-center gap-2 flex-shrink-0">
        <Sparkles className="w-4 h-4 text-indigo-400 flex-shrink-0" />
        <input
          value={aiPrompt}
          onChange={e => setAiPrompt(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAiBuild(); }}
          placeholder="Describe what you want... e.g. 'bar chart of premium by LOB, top 5'"
          className="flex-1 h-8 px-3 rounded-lg bg-background/50 border border-indigo-500/20 text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:ring-1 focus:ring-indigo-500/30"
          disabled={aiBuilding || !modelId}
          data-testid="ai-build-input"
        />
        <Button size="sm" onClick={handleAiBuild} disabled={aiBuilding || !aiPrompt.trim() || !modelId} className="gap-1.5 bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="ai-build-btn">
          {aiBuilding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {aiBuilding ? 'Building...' : 'AI Build'}
        </Button>
        {aiExplanation && (
          <div className="flex items-center gap-2 ml-2 max-w-md">
            <p className="text-[10px] text-indigo-400/70 truncate" title={aiExplanation}>{aiExplanation}</p>
            {aiAlternatives.length > 0 && (
              <div className="flex gap-1 flex-shrink-0">
                {aiAlternatives.map(alt => (
                  <button key={alt} onClick={() => { setVizType(alt); setDirty(true); }} className="text-[8px] px-1.5 py-0.5 rounded bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 transition-colors border border-indigo-500/20" title={`Switch to ${alt.replace('_', ' ')}`}>
                    {alt.replace('_', ' ')}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        {/* Left: Field List — matches Insights builder */}
        {!previewMode && (
        <div className="border-r border-border/50 flex flex-col overflow-hidden flex-shrink-0" style={{ width: fieldsPanelWidth, background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}>
          <div className="p-3 border-b border-border/50">
            <Input value={fieldSearch} onChange={e => setFieldSearch(e.target.value)} placeholder="Search fields..." className="h-7 text-[10px] rounded-lg bg-muted/30 border-border/30" />
          </div>
          {modelId ? (
            <FieldSplitPanel
              filteredDims={filteredDims}
              filteredMsrs={filteredMeasures}
              filteredCalcMsrs={filteredCalcMsrs}
              handleBrowseField={handleBrowseField}
              handleAddFieldToVisual={handleAddFieldToVisual}
              handleViewField={handleViewField}
              handleBinField={handleBinField}
              highlight={fieldSearch}
            />
          ) : (
            <p className="text-xs text-muted-foreground text-center py-8">Select a model first</p>
          )}
        </div>
        )}

        {/* Resize bar for the field-list panel */}
        {!previewMode && (
        <div
          onMouseDown={startFieldsResize}
          data-testid="resizer-fields-panel"
          title="Drag to resize"
          className="group relative w-1.5 shrink-0 cursor-col-resize bg-transparent hover:bg-primary/40 active:bg-primary/60 transition-colors"
        />
        )}

        {/* Middle: Drop Zones + Config — uses DropShelf components */}
        {!previewMode && (
        <div className="w-64 border-r border-border/50 flex flex-col overflow-y-auto flex-shrink-0">
          <div className="p-3 space-y-3">
            {/* Model (read-only after creation) */}
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase">Model</Label>
              <div className="flex items-center gap-2 h-7 mt-0.5 px-2 rounded-lg bg-muted/20 border border-border/20 text-[10px] text-muted-foreground">
                <Database className="w-3 h-3" />
                <span className="truncate">{models.find(m => m.model_id === modelId)?.name || 'No model'}</span>
              </div>
            </div>
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase">Description</Label>
              <Input value={description} onChange={e => { setDescription(e.target.value); setDirty(true); }} placeholder="What this visual shows..." className="h-7 text-[10px] mt-0.5" />
            </div>

            {/* Visualization Type */}
            <div>
              <Label className="text-[9px] text-muted-foreground uppercase">Visualization Type</Label>
              <Select value={vizType} onValueChange={v => { setVizType(v); setDirty(true); }}>
                <SelectTrigger className="h-7 text-[10px] mt-0.5"><SelectValue /></SelectTrigger>
                <SelectContent>{VIZ_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold pt-1">Drop Zones</p>
            <DropShelf label="Category (X-Axis)" icon={Layers} items={rowDims} acceptType="dimension" {...shelfProps}
              onDrop={n => { setRowDims(p => p.includes(n) ? p : [...p, n]); setDirty(true); }}
              onRemove={n => { setRowDims(p => p.filter(x => x !== n)); setDirty(true); }} onReorder={v => { setRowDims(v); setDirty(true); }} />
            <DropShelf label="Color / Series" icon={Layers} items={colDim ? [colDim] : []} acceptType="dimension" {...shelfProps}
              onDrop={n => { setColDim(n); setDirty(true); }}
              onRemove={() => { setColDim(''); setDirty(true); }} />
            <DropShelf label="Values (Y-Axis)" icon={TrendingUp} items={valueMeasures} acceptType="measure" {...shelfProps}
              onDrop={n => { setValueMeasures(p => p.includes(n) ? p : [...p, n]); setDirty(true); }}
              onRemove={n => { setValueMeasures(p => p.filter(x => x !== n)); setDirty(true); }} onReorder={v => { setValueMeasures(v); setDirty(true); }} />
            <DropShelf label="Slicers" icon={Filter} items={filterDims} acceptType="dimension" {...shelfProps}
              onDrop={n => { setFilterDims(p => p.includes(n) ? p : [...p, n]); setDirty(true); }}
              onRemove={n => { setFilterDims(p => p.filter(x => x !== n)); setSlicerSelections(p => { const c = { ...p }; delete c[n]; return c; }); setDirty(true); }}
              onReorder={v => { setFilterDims(v); setDirty(true); }} />

            {/* Slicer Controls */}
            {filterDims.length > 0 && (
              <div className="space-y-2 pt-1">
                {filterDims.map(dim => (
                  <SlicerControl key={dim} dimension={dim} values={slicerValues[dim] || []} selected={slicerSelections[dim]}
                    onChange={val => setSlicerSelections(p => ({ ...p, [dim]: val }))} />
                ))}
              </div>
            )}
          </div>

          <ConfigPanel vizType={vizType} vizConfig={vizConfig}
            onConfigChange={updates => { setVizConfig(prev => ({ ...prev, ...updates })); setDirty(true); }}
            showGrid={showGrid} setShowGrid={v => { setShowGrid(v); setDirty(true); }}
            showLegend={showLegend} setShowLegend={v => { setShowLegend(v); setDirty(true); }}
            refLineValue="" setRefLineValue={() => {}} refLineLabel="" setRefLineLabel={() => {}}
            parameters={[]} allFields={allFieldsCombined} currentVisual={currentVisual} onUpdateVisual={() => {}} />
        </div>
        )}

        {/* Right: Preview */}
        <div className="flex-1 flex flex-col overflow-hidden bg-muted/5">
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border/30">
            <Eye className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-medium text-muted-foreground">Preview</span>
            {queryLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary ml-2" />}
            {chartData.length > 0 && <Badge variant="secondary" className="text-[9px] ml-auto">{chartData.length} rows</Badge>}
          </div>
          <div className="flex-1 p-6 overflow-auto">
            {chartData.length > 0 ? (
              <div ref={previewRef} className="bg-background rounded-xl border p-4 shadow-sm">
                <h3 className="text-sm font-medium mb-3">{name || 'Untitled Visual'}</h3>
                {vizType === 'table' ? (
                  <TableRenderer result={queryResult} vizConfig={vizConfig} formatValue={formatValue} allFields={allFieldsCombined} fieldOverrides={fieldOverrides} rowDims={rowDims} onDrill={() => {}} />
                ) : vizType === 'pivot' ? (
                  <PivotTableRenderer pivotResult={pivotResult} vizConfig={vizConfig} formatValue={formatValue} allFields={allFieldsCombined} onDrill={() => {}} />
                ) : (
                  <div style={{ height: 400 }}>
                    <ChartRenderer vizType={vizType} data={chartDisplayData.slice(0, 100)} rowDims={rowDims} colDim={chartColDim} showGrid={showGrid} showLegend={showLegend} vizConfig={vizConfig} formatValue={formatValue} allFields={allFieldsCombined} fieldOverrides={fieldOverrides} onDrill={() => {}} marks={{}} modelId={modelId} />
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center">
                <BarChart3 className="w-16 h-16 text-muted-foreground/15 mb-4" />
                <p className="text-sm text-muted-foreground">No data configured</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Drag fields from the left panel or double-click to add them</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pill right-click context menu */}
      {pillCtxMenu && (
        <div className="fixed inset-0 z-[100]" onClick={() => setPillCtxMenu(null)} onContextMenu={e => { e.preventDefault(); setPillCtxMenu(null); }}>
          <div className="glass-card rounded-lg shadow-2xl border border-border/50 py-1 w-44" style={{ position: 'fixed', left: pillCtxMenu.x, top: pillCtxMenu.y, zIndex: 101 }} onClick={e => e.stopPropagation()}>
            <button onClick={() => { openFieldProperties(pillCtxMenu.field); setPillCtxMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30 transition-colors">
              Edit Properties
            </button>
            <button onClick={() => { handleBrowseField(allFieldsCombined.find(f => f.name === pillCtxMenu.field) || { name: pillCtxMenu.field }); setPillCtxMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-foreground hover:bg-muted/30 transition-colors">
              Browse Data
            </button>
            <div className="border-t border-border/30 my-1" />
            <button onClick={() => { removeFromAllShelves(pillCtxMenu.field); setPillCtxMenu(null); }} className="w-full flex items-center gap-2 px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 transition-colors">
              Remove
            </button>
          </div>
        </div>
      )}

      {/* Dialogs */}
      <BrowseDataDialog open={browseOpen} onOpenChange={setBrowseOpen} browseField={browseField} browseData={browseData} browseLoading={browseLoading} formatValue={formatValue} />
      <FieldPropertiesDialog open={propsOpen} onOpenChange={setPropsOpen} propsField={propsField?.name} propsForm={propsForm} setPropsForm={setPropsForm} allFields={allFieldsCombined} onSave={saveFieldProperties} />
      <FieldStatsModal modelId={modelId} field={viewDataField} onClose={() => setViewDataField(null)} />
      <BinFieldDialog
        open={!!binField}
        onOpenChange={(v) => { if (!v) setBinField(null); }}
        field={binField}
        modelId={modelId}
        onCreated={loadModelFields}
      />
    </div>
    </TooltipProvider>
  );
}
